import type { SystemRole } from '../rbac/roles';
import type { AuthenticatedUser } from './jwt';

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
}

export interface SignInResponse extends TokenPair {
  user: AuthenticatedUser;
}

/**
 * Carried on auth RPC payloads so one sign-in can be followed across the
 * gateway and auth-service logs. Optional: an older gateway sends none.
 */
export interface RpcTrace {
  requestId?: string;
}

export interface RefreshRequest extends RpcTrace {
  refreshToken: string;
  userAgent?: string;
}

export interface SwitchRoleRequest extends RpcTrace {
  userId: string;
  sessionId: string;
  role: SystemRole;
  /** The caller's refresh cookie, when it has one; the switch consumes it. */
  refreshToken?: string;
}

/** Either field names the session to end; the session id wins when both arrive. */
export interface LogoutRequest extends RpcTrace {
  sessionId?: string;
  refreshToken?: string;
}

/** `POST /auth/logout`: where the browser goes to end the identity provider's own session. */
export interface LogoutResponse {
  /** Null when the provider publishes no end-session endpoint or discovery failed. */
  endSessionUrl: string | null;
}

/** A refresh token presented again within this long of its first use is a race, not theft. */
export const REFRESH_REUSE_INTERVAL_MS = 10_000;

export const SIGN_IN_ERROR_PARAM = 'error';

/** The `error` query parameter on `<SPA origin>/login` after a failed sign-in. */
export const SIGN_IN_ERRORS = {
  cancelled: 'cancelled',
  expired: 'expired',
  exchangeFailed: 'exchange_failed',
  accountDisabled: 'account_disabled',
  /** The Microsoft account matches more than one person here, or one already tied to another account. */
  accountConflict: 'account_conflict',
  unavailable: 'unavailable',
} as const;

export type SignInError = (typeof SIGN_IN_ERRORS)[keyof typeof SIGN_IN_ERRORS];

/**
 * A sign-in whose id token Entra ID issued directly, already verified by
 * auth-service. The key is the object id; the address is used once, to bind a
 * person who has no object id yet to the account that holds that address.
 */
export interface ResolveEntraSignInRequest {
  /** The `oid` claim. Never the pairwise `sub`. */
  entraObjectId: string;
  /**
   * The person's sign-in addresses, most trusted first (one or two: the username and the
   * UPN may differ). Lower-cased and already checked against the allowed domains by the
   * caller. Two that resolve to two accounts are a conflict.
   */
  addresses: [string, ...string[]];
  /** Used only when the person has no account yet. */
  fullName?: string;
  /** The application the sign-in started in, whose best role is activated. */
  targetApplication?: string;
}

/**
 * api-gateway completes the authorization-code + PKCE flow against Microsoft
 * Entra ID as a confidential client, so no browser ever holds a token, and
 * sends the resulting id token here. auth-service verifies it against the
 * tenant's keys, resolves the local user by Entra object id or work address
 * (`USER_PATTERNS.resolveEntraSignIn`), and issues platform tokens carrying RBAC.
 */
export interface OidcTokenExchangeRequest extends RpcTrace {
  idToken: string;
  /** Sent at sign-in start; the id token must echo it. */
  expectedNonce?: string;
  /**
   * The application the sign-in started in (a `SIGN_IN_APPLICATION_KEYS` key, sent by
   * the SPA as `app` and checked at the gateway). The person's best role there is
   * activated. Absent means no target.
   */
  targetApplication?: string;
  userAgent?: string;
  ipAddress?: string;
}

export interface ValidateTokenResponse {
  valid: boolean;
  user: AuthenticatedUser | null;
  reason: 'ok' | 'expired' | 'revoked' | 'malformed';
}
